import {
  FloatingArrow,
  FloatingPortal,
  OffsetOptions,
  Placement,
  arrow,
  autoUpdate,
  offset as floatingUIOffset,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';
import React, { useEffect, useState } from 'react';

import { cn } from 'utils/cn';

type TooltipProps = {
  children: React.ReactElement;
  content: React.ReactNode;
  placement: Placement;
  tooltipClassnames?: string;
  sticky?: boolean;
  show?: boolean;
  showDelay?: number;
  hideDelay?: number;
  offset?: OffsetOptions;
};

const Tooltip = ({
  children,
  content,
  placement,
  show = true,
  tooltipClassnames,
  sticky = false,
  showDelay = 0,
  hideDelay = 0,
  offset = 12,
  className,
  ...props
}: TooltipProps & Omit<JSX.IntrinsicElements['div'], 'content'>) => {
  const [isOpen, setIsOpen] = useState(sticky);
  const arrowRef = React.useRef(null);

  const { x, y, strategy, refs, context } = useFloating({
    placement,
    open: show && isOpen,
    onOpenChange: setIsOpen,
    middleware: [floatingUIOffset(offset ?? 12), arrow({ element: arrowRef })],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        animationFrame: true // Forces position updates on animation frames
      })
  });

  const hover = useHover(context, {
    enabled: show,
    delay: { open: showDelay, close: hideDelay }
  });
  const focus = useFocus(context, { enabled: show });
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, role]);

  useEffect(() => {
    if (!show && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen, show]);

  return (
    <>
      {children &&
        React.cloneElement(children, {
          ref: refs.setReference,
          ...getReferenceProps(),
          ...children.props
        })}
      <FloatingPortal>
        {show && isOpen && (
          <div
            ref={refs.setFloating}
            className={cn('z-2000 shadow-100 rounded-150 px-2 py-1.5 text-body-7 text-icn-txt-prim-inv bg-bg-sec-inv-100-0', className)}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content'
            }}
            {...getFloatingProps()}
            {...props}
          >
            {content}
            <FloatingArrow ref={arrowRef} context={context} className={cn('fill-bg-sec-inv-100-0 w-2 -mb-[3px]', placement.includes('top') && 'mt-[-3px]')} />
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export default Tooltip;
